import { useCallback, useEffect, useMemo, useState } from "react";
import { z, type ZodType } from "zod";
import {
  Button,
  Checkbox,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";

export type ColumnVisibilityColumn<TColumnId extends string> = {
  id: TColumnId;
  label: string;
};

type UseColumnVisibilityParams<TColumnId extends string> = {
  storageKey: string;
  columnIdSchema: ZodType<TColumnId>;
  allColumnIds: readonly TColumnId[];
  defaultVisibleColumnIds: readonly TColumnId[];
};

type UseColumnVisibilityResult<TColumnId extends string> = {
  visibleColumnIds: readonly TColumnId[];
  isColumnVisible: (columnId: TColumnId) => boolean;
  toggleColumn: (columnId: TColumnId) => void;
  setVisibleColumnIds: (ids: readonly TColumnId[]) => void;
  resetColumns: () => void;
};

const buildStorageSchema = <TColumnId extends string>(
  columnIdSchema: ZodType<TColumnId>,
) =>
  z.object({
    visibleColumnIds: z.array(columnIdSchema).nonempty(),
  });

const mapToDistinctVisibleColumnIds = <TColumnId extends string>(
  candidateIds: readonly TColumnId[],
  allowedIds: readonly TColumnId[],
): TColumnId[] => {
  const candidateSet = new Set(candidateIds);
  const orderedIds = allowedIds.filter((id) => candidateSet.has(id));

  return orderedIds.length > 0 ? orderedIds : [...allowedIds];
};

export const useColumnVisibility = <TColumnId extends string>(
  params: UseColumnVisibilityParams<TColumnId>,
): UseColumnVisibilityResult<TColumnId> => {
  const { storageKey, columnIdSchema, allColumnIds, defaultVisibleColumnIds } =
    params;
  const allowedColumnIds = useMemo(() => [...allColumnIds], [allColumnIds]);
  const storageSchema = useMemo(
    () => buildStorageSchema(columnIdSchema),
    [columnIdSchema],
  );

  const [hydrated, setHydrated] = useState(false);
  const [visibleColumnIds, setVisibleColumnIds] = useState<
    readonly TColumnId[]
  >(defaultVisibleColumnIds);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      const storedValue = window.localStorage.getItem(storageKey);

      if (!storedValue) {
        setHydrated(true);
        return;
      }

      const parsed = storageSchema.safeParse(JSON.parse(storedValue));

      if (!parsed.success) {
        window.localStorage.removeItem(storageKey);
        setHydrated(true);
        return;
      }

      setVisibleColumnIds(
        mapToDistinctVisibleColumnIds(
          parsed.data.visibleColumnIds,
          allowedColumnIds,
        ),
      );
      setHydrated(true);
    } catch {
      window.localStorage.removeItem(storageKey);
      setVisibleColumnIds(defaultVisibleColumnIds);
      setHydrated(true);
    }
  }, [allowedColumnIds, defaultVisibleColumnIds, storageKey, storageSchema]);

  useEffect(() => {
    if (!hydrated || typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(
      storageKey,
      JSON.stringify({ visibleColumnIds: [...visibleColumnIds] }),
    );
  }, [hydrated, storageKey, visibleColumnIds]);

  const toggleColumn = useCallback(
    (columnId: TColumnId) => {
      setVisibleColumnIds((current) => {
        const isVisible = current.includes(columnId);

        if (isVisible) {
          if (current.length === 1) {
            return current;
          }

          return mapToDistinctVisibleColumnIds(
            current.filter((id) => id !== columnId),
            allowedColumnIds,
          );
        }

        return mapToDistinctVisibleColumnIds(
          [...current, columnId],
          allowedColumnIds,
        );
      });
    },
    [allowedColumnIds],
  );

  const setVisibleColumnIdsSafe = useCallback(
    (ids: readonly TColumnId[]) => {
      setVisibleColumnIds(mapToDistinctVisibleColumnIds(ids, allowedColumnIds));
    },
    [allowedColumnIds],
  );

  const resetColumns = useCallback(() => {
    setVisibleColumnIds([...defaultVisibleColumnIds]);
  }, [defaultVisibleColumnIds]);

  const isColumnVisible = useCallback(
    (columnId: TColumnId) => visibleColumnIds.includes(columnId),
    [visibleColumnIds],
  );

  return {
    visibleColumnIds,
    isColumnVisible,
    toggleColumn,
    setVisibleColumnIds: setVisibleColumnIdsSafe,
    resetColumns,
  };
};

type ColumnVisibilityControlProps<TColumnId extends string> = {
  columns: readonly ColumnVisibilityColumn<TColumnId>[];
  visibleColumnIds: readonly TColumnId[];
  onToggleColumn: (columnId: TColumnId) => void;
  onResetColumns?: () => void;
  disabled?: boolean;
  buttonLabel?: string;
};

export const ColumnVisibilityControl = <TColumnId extends string>(
  props: ColumnVisibilityControlProps<TColumnId>,
) => {
  const {
    columns,
    visibleColumnIds,
    onToggleColumn,
    onResetColumns,
    disabled = false,
    buttonLabel = "Columns",
  } = props;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpenMenu = useCallback<React.MouseEventHandler<HTMLElement>>(
    (event) => {
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const isOnlyVisibleColumn = useCallback(
    (columnId: TColumnId) =>
      visibleColumnIds.length === 1 && visibleColumnIds[0] === columnId,
    [visibleColumnIds],
  );

  return (
    <>
      <Button
        onClick={handleOpenMenu}
        disabled={disabled}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        aria-controls={open ? "column-visibility-menu" : undefined}
        startIcon={<ViewColumnIcon />}
      >
        {buttonLabel}
      </Button>
      <Menu
        id="column-visibility-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {columns.map((column) => {
          const isColumnDisabled = isOnlyVisibleColumn(column.id);
          const checked = visibleColumnIds.includes(column.id);

          return (
            <MenuItem
              key={column.id}
              onClick={() => {
                onToggleColumn(column.id);
              }}
              disabled={isColumnDisabled && checked}
            >
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  checked={checked}
                  tabIndex={-1}
                  disableRipple
                  disabled={isColumnDisabled && checked}
                />
              </ListItemIcon>
              <ListItemText primary={column.label} />
            </MenuItem>
          );
        })}
        {onResetColumns && (
          <MenuItem
            onClick={() => {
              onResetColumns();
            }}
          >
            <ListItemText primary="Reset to defaults" />
          </MenuItem>
        )}
      </Menu>
    </>
  );
};
